import React, { useEffect, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import ShieldIcon from '@mui/icons-material/Shield';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { api, type PropertyScan } from '../services/api';
import { CONDITION_SCORE_COLOR } from '../constants/propertyScan';

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2 }}>
      <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ color: '#8A8F98', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>{label}</Typography>
          <Typography sx={{ color: '#F5F7FA', fontSize: 30, fontWeight: 'bold' }}>{value}</Typography>
        </Box>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(0,170,255,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00AAFF',
        }}>
          {icon}
        </Box>
      </CardContent>
    </Card>
  );
}

function mostCommon(values: string[]): string | null {
  if (values.length === 0) return null;
  const counts = new Map<string, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [scans, setScans] = useState<PropertyScan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getPropertyScans().then(setScans).finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: '#00AAFF' }} /></Box>;

  const scoredScans = scans.filter(s => s.condition_score != null);
  const avgCondition = scoredScans.length
    ? Math.round(scoredScans.reduce((sum, s) => sum + (s.condition_score ?? 0), 0) / scoredScans.length)
    : null;
  const totalSqft = scans.reduce((sum, s) => sum + (s.square_footage ?? 0), 0);
  const costedScans = scans.filter(s => s.cost_prediction);
  const avgCost = costedScans.length
    ? Math.round(costedScans.reduce((sum, s) => sum + (s.cost_prediction?.average ?? 0), 0) / costedScans.length)
    : null;
  const topLocation = mostCommon(scans.map(s => s.location).filter((l): l is string => Boolean(l)));

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ color: '#F5F7FA', fontWeight: 'bold' }}>
            The Data Behind Every Repaint Decision
          </Typography>
          <Typography sx={{ color: '#8A8F98', fontSize: 13 }}>Scan. Analyze. Predict. Optimize.</Typography>
        </Box>
        <Button variant="contained" startIcon={<CameraAltIcon />} onClick={() => navigate('/property-scans/new')}
          sx={{ bgcolor: '#00AAFF', borderRadius: 999, px: 2.5, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#0090DD' } }}>
          New Scan
        </Button>
      </Box>

      {/* KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Property Scans" value={scans.length} icon={<CameraAltIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Avg Condition" value={avgCondition != null ? `${avgCondition}/100` : '—'} icon={<ShieldIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Total Sq. Ft." value={totalSqft > 0 ? totalSqft.toLocaleString() : '—'} icon={<TrackChangesIcon fontSize="small" />} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Avg Project Cost" value={avgCost != null ? `$${Math.round(avgCost / 1000)}K` : '—'} icon={<AttachMoneyIcon fontSize="small" />} />
        </Grid>
      </Grid>

      {/* Market Price Intelligence */}
      <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography sx={{ color: '#00AAFF', fontSize: 12, fontWeight: 700, letterSpacing: 0.5, mb: 0.5 }}>
            MARKET PRICE INTELLIGENCE
          </Typography>
          {topLocation ? (
            <>
              <Typography sx={{ color: '#F5F7FA', fontSize: 16, fontWeight: 600 }}>{topLocation}</Typography>
              <Typography sx={{ color: '#6E7681', fontSize: 12, mt: 0.5 }}>
                Based on {scans.filter(s => s.location === topLocation).length} scan(s) in this area.
              </Typography>
            </>
          ) : (
            <Typography sx={{ color: '#6E7681', fontSize: 13 }}>
              No location data yet — add a location when editing a scan to see market pricing here.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Recent scans */}
      <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 2, mb: 3 }}>
        <Typography sx={{ color: '#8A8F98', fontSize: 13, mb: 2 }}>Recent Scans</Typography>
        {scans.length === 0 ? (
          <Typography sx={{ color: '#6E7681', fontSize: 13, textAlign: 'center', py: 3 }}>
            No scans yet. Start a new scan to get AI-powered repaint insights.
          </Typography>
        ) : scans.slice(0, 8).map(scan => (
          <Box key={scan.id} onClick={() => navigate(`/property-scans/${scan.id}`)}
            sx={{ display: 'flex', alignItems: 'center', py: 1, borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#F5F7FA', fontSize: 14 }}>{scan.property_name}</Typography>
              <Typography sx={{ color: '#6E7681', fontSize: 12 }}>{new Date(scan.created_at).toLocaleDateString()}</Typography>
            </Box>
            {scan.condition_score != null && (
              <Typography sx={{ color: CONDITION_SCORE_COLOR(scan.condition_score), fontWeight: 700, fontSize: 14 }}>
                {scan.condition_score}/100
              </Typography>
            )}
          </Box>
        ))}
      </Card>

      <PinSecurityCard />
    </Box>
  );
}

function PinSecurityCard() {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const close = () => { setOpen(false); setPin(''); setConfirmPin(''); setError(''); };

  const save = async () => {
    if (!/^\d{4,6}$/.test(pin)) { setError('PIN must be 4–6 digits.'); return; }
    if (pin !== confirmPin) { setError('PINs do not match.'); return; }
    setSaving(true); setError('');
    try {
      await api.setPin(pin);
      toast.success('PIN saved.');
      close();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to save PIN.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true); setError('');
    try {
      await api.clearPin();
      toast.success('PIN removed.');
      close();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to remove PIN.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card sx={{ bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ color: '#F5F7FA', fontSize: 14, fontWeight: 600 }}>Quick Login PIN</Typography>
            <Typography sx={{ color: '#6E7681', fontSize: 12, mt: 0.5 }}>
              Set a 4–6 digit PIN to sign in faster on this device.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => setOpen(true)}
            sx={{ borderColor: 'rgba(255,255,255,0.15)', color: '#F5F7FA', borderRadius: 999, textTransform: 'none', '&:hover': { borderColor: '#00AAFF' } }}>
            Manage PIN
          </Button>
        </Box>
      </Card>

      <Dialog open={open} onClose={close} PaperProps={{ sx: { bgcolor: '#121417', border: '1px solid rgba(255,255,255,0.08)' } }}>
        <DialogTitle sx={{ color: '#F5F7FA' }}>Manage Login PIN</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            fullWidth label="New PIN (4–6 digits)" type="password" value={pin}
            onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { color: '#F5F7FA' } }}
            InputLabelProps={{ sx: { color: '#6E7681' } }}
          />
          <TextField
            fullWidth label="Confirm PIN" type="password" value={confirmPin}
            onChange={e => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 6 }}
            sx={{ '& .MuiOutlinedInput-root': { color: '#F5F7FA' } }}
            InputLabelProps={{ sx: { color: '#6E7681' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={clear} disabled={saving} sx={{ color: '#EF4444', mr: 'auto' }}>Remove PIN</Button>
          <Button onClick={close} disabled={saving} sx={{ color: '#8A8F98' }}>Cancel</Button>
          <Button onClick={save} disabled={saving} variant="contained"
            sx={{ bgcolor: '#00AAFF', borderRadius: 999, textTransform: 'none', '&:hover': { bgcolor: '#0090DD' } }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
